import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, firstValueFrom } from 'rxjs';
import { AgentFeatureService } from '../agent-feature/agent-feature.service';
import { ConfigurationService } from '../../../../services/configuration/configuration.service';
import { LoggerService } from '../../../../services/logger-services/logger.service';
import { AgentConfigService } from '../agent-config/agent-config.service';
import { StreamingStateService } from '../../../../shared/services/streaming-state/streaming-state.service';
import { DeleteAgentThreadService } from '../agent-threads/delete-agent-thread/delete-agent-thread';
import { GetAgentThreadsService } from '../agent-threads/get-agent-threads/get-agent-threads.service';
import { GetAgentThreadsRequest, AgentThread } from '../../models';
import { environment } from '../../../../environments/environment';
import { RegulationsHubPromptLibraryStore } from '../regulations-hub-prompt-library/regulations-hub-prompt-library.store';

/**
 * Agent Chat Message Interface
 * Represents a single message in the agent chat history
 */
export interface AgentChatMessage {
  question: string;
  answer: string;
  pills?: string[];
  isLoading?: boolean;
  image_visualization?: string;
  metadata?: any;
  errorMessage?: string;
  updated_at?: string; // ISO 8601 format timestamp
  completedReasoningSteps?: any[]; // Store completed reasoning steps for history display
  response_id?: string; // Backend pk_chathistoryid, used for single-response share
  /** S3 paths from saved answer (RIA impact assessment reports, etc.) */
  file_paths?: string[];
  retrieval_mode?: 'latest' | 'all';
  is_like?: boolean | null; // Feedback: true = liked, false = disliked, null/undefined = no feedback
  feedback?: any; // Feedback options dict (for dislike)
  comments?: string; // Additional feedback comments
}

/**
 * Prompt Library Item Interface
 * Represents a suggested prompt in the agent's prompt library
 */
export interface PromptLibraryItem {
  text: string;
  category?: string;
}

/**
 * Session Creation Event Interface
 * Event emitted when a new agent session is created
 */
export interface SessionCreationEvent {
  threadName: string;
  threadId: string;
  agentId: string; // Agent that owns this session – prevents cross-agent leaking
}

/**
 * Event emitted when an agent session should be removed
 */
export interface SessionRemovalEvent {
  threadId: string;
}

/** Virtual agent id for embedding AI Workbench (iframe); not a chat agent. */
export const AI_WORKBENCH_AGENT_ID = 'ai_workbench';

/**
 * Agent Orchestrator Service
 * 
 * Central orchestrator for all agent-related state management and logic.
 * This service encapsulates agent behavior to keep components clean and focused.
 * 
 * Responsibilities:
 * - Managing current agent selection
 * - Controlling agent landing page visibility  
 * - Loading and caching agent prompt libraries
 * - Managing agent chat history
 * - Coordinating agent switches with confirmation dialogs
 * - Triggering session creation events
 * 
 * Usage:
 * Inject this service into components that need agent functionality.
 * Subscribe to observables for reactive state updates.
 */
@Injectable({
  providedIn: 'root'
})
export class AgentOrchestratorService {
  
  private readonly currentAgentId = new BehaviorSubject<string>('module');
  public currentAgentId$ = this.currentAgentId.asObservable();
  
  private readonly newSessionCreated = new Subject<SessionCreationEvent>();
  public newSessionCreated$ = this.newSessionCreated.asObservable();
  
  private readonly sessionRemovalRequested = new Subject<SessionRemovalEvent>();
  public sessionRemovalRequested$ = this.sessionRemovalRequested.asObservable();
  
  private readonly showAgentLanding = new BehaviorSubject<boolean>(false);
  public showAgentLanding$ = this.showAgentLanding.asObservable();
  
  // Agent Hub visibility state - when true, shows agent hub instead of agent chat
  // Default to true so the Agent Hub is shown on initial load
  private readonly showAgentHub = new BehaviorSubject<boolean>(true);
  public showAgentHub$ = this.showAgentHub.asObservable();
  
  private readonly agentPromptLibrary = new BehaviorSubject<PromptLibraryItem[]>([]);
  public agentPromptLibrary$ = this.agentPromptLibrary.asObservable();
  
  private readonly agentChatHistory = new BehaviorSubject<AgentChatMessage[]>([]);
  public agentChatHistory$ = this.agentChatHistory.asObservable();
  
  private readonly isNewAgentChat = new BehaviorSubject<boolean>(true);
  public isNewAgentChat$ = this.isNewAgentChat.asObservable();
  
  private currentThreadId: string | null = null;
  /** Emits when current thread ID changes (sidebar uses this to highlight selected session when switching from header) */
  private readonly currentThreadIdSubject = new BehaviorSubject<string | null>(null);
  public currentThreadId$ = this.currentThreadIdSubject.asObservable();
  /** Display name for current thread when just created (so session list can show it if sidebar was closed) */
  private currentThreadName: string | null = null;
  private pendingAgentSwitch: string | null = null;
  
  // Track which agents have had their threads loaded (persists across component lifecycle)
  private readonly agentsWithLoadedThreads = new Set<string>();
  // Track which agents currently have a threads API call in flight (prevents duplicate requests)
  private readonly agentsWithLoadingInProgress = new Set<string>();
  // Emits agentId when threads finish loading (so late subscribers can pick up cached data)
  private readonly agentThreadsReadySubject = new Subject<string>();
  public agentThreadsReady$ = this.agentThreadsReadySubject.asObservable();
  
  // Cache agent threads to restore when component is recreated (collapse/expand)
  private readonly cachedAgentThreads = new Map<string, any[]>();
  // Total thread count from first load (so lazy load does not change the displayed count)
  private readonly cachedTotalThreadsCount = new Map<string, number>();
  
  // Streaming confirmation popup state
  private readonly showStreamingConfirmationPopup = new BehaviorSubject<boolean>(false);
  public showStreamingConfirmationPopup$ = this.showStreamingConfirmationPopup.asObservable();
  
  private pendingAction: (() => void) | null = null;
  private streamingConfirmationType: 'new_chat' | 'session_switch' | 'chatbot_switch' | 'agent_switch' = 'new_chat';
  private lazyLoadHistoryCallback: (() => boolean) | null = null;
  private readonly lazyLoadState = new BehaviorSubject<{isLoading: boolean, hasMore: boolean}>({isLoading: false, hasMore: false});
  public lazyLoadState$ = this.lazyLoadState.asObservable();
  private historyCurrentPage: number = 1;
  private historyTotalPages: number = 1;
  /** True while loading a session's history (switch session from sidebar). */
  private readonly isLoadingSession = new BehaviorSubject<boolean>(false);
  public isLoadingSession$ = this.isLoadingSession.asObservable();
  private stopStreamingCallback: (() => void) | null = null;

  constructor(
    private readonly agentFeatureService: AgentFeatureService,
    private readonly configurationService: ConfigurationService,
    private readonly logger: LoggerService,
    private readonly agentConfigService: AgentConfigService,
    private readonly streamingStateService: StreamingStateService,
    private readonly deleteAgentThreadService: DeleteAgentThreadService,
    private readonly getAgentThreadsService: GetAgentThreadsService,
    private regulationsHubPromptLibraryStore: RegulationsHubPromptLibraryStore
  ) {}

  /**
   * Get current agent ID
   * 
   * @returns string - Current agent ID ('module' for default chatbot)
   */
  getCurrentAgentId(): string {
    try {
      return this.currentAgentId.value;
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error getting current agent ID', 'getCurrentAgentId', error);
      return 'module';
    }
  }

  /**
   * Check if threads have already been loaded for an agent
   * Used to prevent duplicate API calls when panel is collapsed/expanded
   * 
   * @param agentId - Agent ID to check
   * @returns boolean - True if threads have been loaded for this agent
   */
  hasAgentThreadsLoaded(agentId: string): boolean {
    return this.agentsWithLoadedThreads.has(agentId);
  }

  /**
   * Mark an agent as having its threads loaded
   * Called after successfully loading threads for an agent
   * 
   * @param agentId - Agent ID to mark as loaded
   */
  markAgentThreadsLoaded(agentId: string): void {
    this.agentsWithLoadedThreads.add(agentId);
  }

  /**
   * Check if threads are currently being loaded for an agent
   * Used to prevent duplicate API calls when multiple code paths trigger loading
   */
  isAgentThreadsLoading(agentId: string): boolean {
    return this.agentsWithLoadingInProgress.has(agentId);
  }

  /**
   * Mark an agent's threads as currently loading (API call in flight)
   */
  markAgentThreadsLoading(agentId: string): void {
    this.agentsWithLoadingInProgress.add(agentId);
  }

  /**
   * Clear the loading-in-progress flag for an agent
   */
  clearAgentThreadsLoading(agentId: string): void {
    this.agentsWithLoadingInProgress.delete(agentId);
  }

  /**
   * Clear the threads loaded flag for an agent
   * Used when threads need to be reloaded (e.g., after creating/deleting a thread)
   * 
   * @param agentId - Agent ID to clear, or undefined to clear all
   */
  clearAgentThreadsLoaded(agentId?: string): void {
    if (agentId) {
      this.agentsWithLoadedThreads.delete(agentId);
      this.agentsWithLoadingInProgress.delete(agentId);
      this.cachedAgentThreads.delete(agentId);
      this.cachedTotalThreadsCount.delete(agentId);
      this.cachedThreadsPagination.delete(agentId);
    } else {
      this.agentsWithLoadedThreads.clear();
      this.agentsWithLoadingInProgress.clear();
      this.cachedAgentThreads.clear();
      this.cachedTotalThreadsCount.clear();
      this.cachedThreadsPagination.clear();
    }
  }

  /**
   * Cache agent threads pagination state (for restoring scroll position and lazy loading state)
   */
  private readonly cachedThreadsPagination = new Map<string, { currentPage: number; totalPages: number; hasMorePages: boolean; pageSize?: number }>();

  /**
   * Cache agent threads for persistence across component lifecycle
   * 
   * @param agentId - Agent ID to cache threads for
   * @param threads - Array of threads to cache
   * @param totalThreadsCount - Total thread count from first load (so lazy load does not change displayed count)
   * @param pagination - Optional pagination state to cache
   */
  cacheAgentThreads(agentId: string, threads: any[], totalThreadsCount?: number, pagination?: { currentPage: number; totalPages: number; hasMorePages: boolean; pageSize?: number }): void {
    this.cachedAgentThreads.set(agentId, threads);
    if (totalThreadsCount !== undefined && totalThreadsCount !== null) {
      this.cachedTotalThreadsCount.set(agentId, totalThreadsCount);
    }
    if (pagination) {
      this.cachedThreadsPagination.set(agentId, pagination);
    }
  }

  /**
   * Get cached agent threads
   * 
   * @param agentId - Agent ID to get cached threads for
   * @returns Cached threads array or null if not cached
   */
  getCachedAgentThreads(agentId: string): any[] | null {
    return this.cachedAgentThreads.get(agentId) || null;
  }

  /**
   * Get cached total thread count (from first load) for display; does not change when lazy loading
   */
  getCachedTotalThreadsCount(agentId: string): number | null {
    const n = this.cachedTotalThreadsCount.get(agentId);
    return n !== undefined ? n : null;
  }

  /**
   * Get cached pagination state for an agent
   * 
   * @param agentId - Agent ID to get cached pagination for
   * @returns Cached pagination state or null if not cached
   */
  getCachedThreadsPagination(agentId: string): { currentPage: number; totalPages: number; hasMorePages: boolean; pageSize?: number } | null {
    return this.cachedThreadsPagination.get(agentId) || null;
  }

  /**
   * Check if agent landing page should be displayed
   * 
   * @param isNewChat - Whether this is a new chat session
   * @param chatQueriesListLength - Number of existing chat queries
   * @returns boolean - True if landing should be shown
   */
  shouldShowAgentLanding(isNewChat: boolean, chatQueriesListLength: number): boolean {
    try {
      if (!this.agentFeatureService.isAvailable()) {
        return false;
      }
      
      return this.showAgentLanding.value && 
             isNewChat && 
             this.currentAgentId.value !== 'module' &&
             chatQueriesListLength === 0;
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error checking landing visibility', 'shouldShowAgentLanding', error);
      return false;
    }
  }

  /**
   * Handle agent selection from sidebar
   * Determines if confirmation is needed before switching
   * 
   * @param agentId - ID of the agent to switch to
   * @param isStreamingOrProcessing - Whether there's ongoing activity
   * @returns boolean - True if confirmation popup needed, false if switched immediately
   */
  handleAgentSelection(agentId: string, isStreamingOrProcessing: boolean): boolean {
    if (isStreamingOrProcessing) {
      this.pendingAgentSwitch = agentId;
      return true;
    }
    
    this.switchToAgent(agentId).catch((error: any) => {
      this.logger.logInfo('[AgentOrchestrator] Error handling agent selection', 'handleAgentSelection', error);
    });
    return false;
  }

  /**
   * Confirm and execute pending agent switch
   * 
   * @returns string | null - Agent ID that was switched to, or null
   */
  confirmPendingSwitch(): string | null {
    if (this.pendingAgentSwitch) {
      const agentId = this.pendingAgentSwitch;
      this.pendingAgentSwitch = null;
      this.switchToAgent(agentId).catch((error: any) => {
        this.logger.logInfo('[AgentOrchestrator] Error confirming pending switch', 'confirmPendingSwitch', error);
      });
      return agentId;
    }
    return null;
  }

  /**
   * Cancel pending agent switch
   */
  cancelPendingSwitch(): void {
    try {
      this.pendingAgentSwitch = null;
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error canceling pending switch', 'cancelPendingSwitch', error);
    }
  }

  /**
   * Switch to specified agent
   * Loads agent configuration and prompt library
   * 
   * @param agentId - ID of agent to switch to
   * @returns Promise<void>
   */
  async switchToAgent(agentId: string): Promise<void> {
    try {
      // Hide agent hub when switching to a specific agent
      this.showAgentHub.next(false);
      
      if (agentId === 'module') {
        this.currentAgentId.next(agentId);
        this.showAgentLanding.next(false);
        this.isNewAgentChat.next(false);
        this.agentPromptLibrary.next([]);
        return;
      }
      
      if (!this.agentFeatureService.isAvailable()) {
        return;
      }
      
      const isAgentEnabled = await this.configurationService.isAgentEnabled(agentId);
      if (!isAgentEnabled) {
        this.currentAgentId.next('module');
        this.showAgentLanding.next(false);
        this.isNewAgentChat.next(false);
        this.agentPromptLibrary.next([]);
        return;
      }
      
      this.currentThreadId = null;
      this.currentThreadIdSubject.next(null);
      this.clearAgentChatHistory(); // Fresh state so next question creates new session, not follow-up

      this.currentAgentId.next(agentId);
      this.showAgentLanding.next(true);
      this.isNewAgentChat.next(true); // Show landing page when switching to agent
      await this.loadAgentPromptLibrary(agentId);
      // Load threads as soon as agent is selected so header dropdown and sidebar have sessions (no wait for opening collapse)
      this.loadThreadsForAgentIfNeeded(agentId);
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error switching agent', 'switchToAgent', error);
    }
  }

  /**
   * Load threads for an agent if not already loaded.
   * Called when user selects an agent (from hub or sidebar) so header "New Topic" dropdown and session list have data without waiting for sidebar open.
   */
  loadThreadsForAgentIfNeeded(agentId: string): void {
    try {
      if (!agentId || agentId === 'module' || agentId === 'agent_hub' || agentId === AI_WORKBENCH_AGENT_ID) return;
      if (this.agentsWithLoadedThreads.has(agentId)) return;
      if (this.agentsWithLoadingInProgress.has(agentId)) return;

      const tenantID = environment.TenantId || '';
      const userID = environment.LoginUserID || '';
      if (!tenantID || !userID) {
        this.logger.logInfo('[AgentOrchestrator] Skip threads load: missing tenant_id or user_id', 'loadThreadsForAgentIfNeeded');
        return;
      }

      this.agentsWithLoadingInProgress.add(agentId);

      const config = this.agentConfigService.getAgentConfig(agentId);
      const pageSize = config?.thread_page_size && config.thread_page_size > 0
        ? config.thread_page_size
        : this.calculateDynamicPageSize(config);
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      const request: GetAgentThreadsRequest = {
        tenant_id: tenantID,
        user_id: userID,
        page: 1,
        page_size: pageSize
      };

      this.getAgentThreadsService.getAgentThreads(request, agentId).subscribe({
        next: (response) => {
          this.agentsWithLoadingInProgress.delete(agentId);
          if (response.status === 'success' && response.data) {
            const threads = response.data.threads.map((t: AgentThread) => ({
              thread_id: t.thread_id,
              thread_name: t.thread_name || 'Untitled Conversation',
              agent_model: agentId,
              message_count: 0,
              created_at: t.updated_at || new Date().toISOString(),
              modified_at: t.updated_at || new Date().toISOString(),
              last_question: t.thread_name || 'No messages yet',
              is_selected: false
            }));
            const totalThreadsCount = response.data.pagination?.total_threads ?? threads.length;
            const pagination = response.data.pagination
              ? {
                  currentPage: response.data.pagination.current_page || 1,
                  totalPages: response.data.pagination.total_pages || 1,
                  hasMorePages: (response.data.pagination.current_page || 1) < (response.data.pagination.total_pages || 1),
                  pageSize
                }
              : { currentPage: 1, totalPages: 1, hasMorePages: false, pageSize };
            this.agentsWithLoadedThreads.add(agentId);
            this.cacheAgentThreads(agentId, threads, totalThreadsCount, pagination);
            this.agentThreadsReadySubject.next(agentId);
            this.logger.logInfo(
              `[AgentOrchestrator] Loaded ${threads.length} threads for ${agentId} (total: ${totalThreadsCount})`,
              'loadThreadsForAgentIfNeeded'
            );
          }
        },
        error: (err) => {
          this.agentsWithLoadingInProgress.delete(agentId);
          this.agentThreadsReadySubject.next(agentId);
          this.logger.logInfo('[AgentOrchestrator] Error loading threads for agent', 'loadThreadsForAgentIfNeeded', err);
        }
      });
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error in loadThreadsForAgentIfNeeded', 'loadThreadsForAgentIfNeeded', error);
    }
  }

  private static readonly DEFAULT_PAGE_SIZE = 20;
  private static readonly MAX_PAGE_SIZE = 100;
  private static readonly THREAD_ITEM_HEIGHT = 40;
  private static readonly BUFFER_MULTIPLIER = 1.5;

  /**
   * Calculate dynamic page size based on viewport height.
   * Mirrors the logic in SessionHistoryComponent but without DOM element access.
   */
  private calculateDynamicPageSize(config?: any): number {
    try {
      const minPageSize = config?.thread_page_size_min || AgentOrchestratorService.DEFAULT_PAGE_SIZE;
      const maxPageSize = config?.thread_page_size_max || AgentOrchestratorService.MAX_PAGE_SIZE;
      const availableHeight = Math.max(window.innerHeight - 242, 200);
      const itemsInViewport = Math.floor(availableHeight / AgentOrchestratorService.THREAD_ITEM_HEIGHT);
      const calculatedSize = Math.floor(itemsInViewport * AgentOrchestratorService.BUFFER_MULTIPLIER);
      return Math.max(minPageSize, Math.min(calculatedSize, maxPageSize));
    } catch {
      return AgentOrchestratorService.DEFAULT_PAGE_SIZE;
    }
  }

  /**
   * Load prompt library for specified agent
   * 
   * @param agentId - ID of agent
   * @returns Promise<void>
   */
  private async loadAgentPromptLibrary(agentId: string): Promise<void> {
    try {
      if (!this.agentFeatureService.isAvailable()) {
        this.agentPromptLibrary.next([]);
        return;
      }

      // Use centralized config service (already loaded at app startup)
      const agent = this.agentConfigService.getAgentConfig(agentId);
      
      if (agent && agent.prompt_library) {
        this.agentPromptLibrary.next(agent.prompt_library as any);
      } else {
        this.agentPromptLibrary.next([]);
      }
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error loading prompt library', 'loadAgentPromptLibrary', error);
      this.agentPromptLibrary.next([]);
    }
  }

  /**
   * Reset agent state for new chat
   * Keeps current agent but resets landing visibility and clears thread ID.
   * Stops any active streaming so the send button is enabled (e.g. after delete during stream).
   */
  resetAgentState(): void {
    try {
      if (this.stopStreamingCallback) {
        this.stopStreamingCallback();
      } else {
        this.streamingStateService.setStreamingState(false);
      }
      const currentAgent = this.currentAgentId.value;
      this.showAgentLanding.next(currentAgent !== 'module');
      
      // Clear thread ID for new chat (similar to chatbot clearing sessionIdArray)
      this.currentThreadId = null;
      this.currentThreadIdSubject.next(null);
      
      // Reset pagination state
      this.historyCurrentPage = 1;
      this.historyTotalPages = 1;
      
      // Set to new chat state
      this.isNewAgentChat.next(true);
      
      this.logger.logInfo('[AgentOrchestrator] Agent state reset for new chat', 'resetAgentState');
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error resetting agent state', 'resetAgentState', error);
    }
  }

  /**
   * Hide agent landing page
   */
  hideAgentLanding(): void {
    try {
      this.showAgentLanding.next(false);
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error hiding landing', 'hideAgentLanding', error);
    }
  }

  /**
   * Show agent landing page (e.g. when selected thread has no history)
   */
  showAgentLandingPage(): void {
    try {
      this.showAgentLanding.next(true);
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error showing landing', 'showAgentLandingPage', error);
    }
  }

  /**
   * Show the Agent Hub (list of all agents)
   * Sets a special agent mode to display the hub instead of individual agent
   */
  navigateToAgentHub(): void {
    try {
      this.regulationsHubPromptLibraryStore.clearHubRegRadarSlice();
      this.showAgentHub.next(true);
      this.currentAgentId.next('agent_hub');
      this.showAgentLanding.next(false);
      this.logger.logInfo('[AgentOrchestrator] Navigating to agent hub', 'navigateToAgentHub');
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error navigating to agent hub', 'navigateToAgentHub', error);
    }
  }

  /**
   * Show AI Workbench embedded app (iframe URL from config).
   */
  navigateToAiWorkbench(): void {
    this.showAgentHub.next(false);
    this.currentAgentId.next(AI_WORKBENCH_AGENT_ID);
    this.showAgentLanding.next(false);
    this.logger.logInfo('[AgentOrchestrator] Navigating to AI Workbench', 'navigateToAiWorkbench');
  }

  /**
   * Check if agent hub is currently shown
   * 
   * @returns boolean - True if agent hub is visible
   */
  isAgentHubVisible(): boolean {
    return this.showAgentHub.value;
  }

  /**
   * Get prompt library for current agent
   * 
   * @returns PromptLibraryItem[] - Array of prompt suggestions
   */
  getPromptLibrary(): PromptLibraryItem[] {
    try {
      return this.agentPromptLibrary.value;
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error getting prompt library', 'getPromptLibrary', error);
      return [];
    }
  }

  /**
   * Reset to default module (no agent)
   * Clears all agent-related state and stops any streaming
   */
  resetToModule(): void {
    try {
      // Stop any active streaming first (critical fix for agent-to-chatbot switch)
      if (this.stopStreamingCallback) {
        this.stopStreamingCallback();
      }
      this.streamingStateService.setStreamingState(false);
      
      this.currentAgentId.next('module');
      this.showAgentLanding.next(false);
      this.agentPromptLibrary.next([]);
      this.pendingAgentSwitch = null;
      this.currentThreadId = null;
      this.currentThreadIdSubject.next(null);
      this.clearAgentChatHistory();
      
      // Clear streaming confirmation state
      this.showStreamingConfirmationPopup.next(false);
      this.pendingAction = null;
      this.streamingConfirmationType = 'new_chat';
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error resetting to module', 'resetToModule', error);
    }
  }

  /**
   * Add new message to agent chat history
   * 
   * @param question - User's question
   */
  addAgentMessage(question: string, retrievalMode?: 'latest' | 'all'): void {
    try {
      const newMessage: AgentChatMessage = {
        question: question,
        answer: '',
        pills: [],
        isLoading: true,
        ...(retrievalMode ? { retrieval_mode: retrievalMode } : {})
      };
      
      const currentHistory = this.agentChatHistory.value;
      this.agentChatHistory.next([...currentHistory, newMessage]);
      
      this.isNewAgentChat.next(false);
      this.hideAgentLanding();
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error adding agent message', 'addAgentMessage', error);
    }
  }

  /**
   * Update latest message in chat history
   * 
   * @param answer - Agent's answer
   * @param pills - Follow-up suggestions
   * @param imageVisualization - Image visualization data
   * @param metadata - Additional metadata
   * @param errorMessage - Error message if any
   * @param isStreaming - Whether response is still streaming
   * @param updatedAt - Timestamp when response was completed (ISO 8601 format)
   * @param completedReasoningSteps - Completed reasoning steps for history display
   */
  updateLatestAgentMessage(
    answer: string, 
    pills?: string[],
    imageVisualization?: string,
    metadata?: any,
    errorMessage?: string,
    isStreaming: boolean = false,
    updatedAt?: string,
    completedReasoningSteps?: any[],
    response_id?: string
  ): void {
    try {
      const currentHistory = this.agentChatHistory.value;
      if (currentHistory.length === 0) return;
      
      const updatedHistory = [...currentHistory];
      const latestMessage = updatedHistory[updatedHistory.length - 1];
      latestMessage.answer = answer;
      
      // Only use pills from streaming response, no auto-generation
      if (pills && pills.length > 0) {
        latestMessage.pills = pills;
      } else {
        latestMessage.pills = [];
      }
      
      latestMessage.isLoading = isStreaming;
      
      if (imageVisualization !== undefined) {
        latestMessage.image_visualization = imageVisualization;
      }
      if (metadata !== undefined) {
        latestMessage.metadata = metadata;
      }
      if (errorMessage !== undefined) {
        latestMessage.errorMessage = errorMessage;
      }
      
      // Set timestamp when streaming completes
      if (updatedAt !== undefined) {
        latestMessage.updated_at = updatedAt;
      } else if (!isStreaming && !latestMessage.updated_at) {
        // Auto-set timestamp when streaming completes if not provided
        latestMessage.updated_at = new Date().toISOString();
      }
      
      // Save completed reasoning steps for history display
      if (completedReasoningSteps !== undefined) {
        latestMessage.completedReasoningSteps = completedReasoningSteps;
      }
      if (response_id !== undefined) {
        latestMessage.response_id = response_id;
      }
      
      this.agentChatHistory.next(updatedHistory);
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error updating message', 'updateLatestAgentMessage', error);
    }
  }

  /**
   * Generate context-aware suggestion pills based on response content
   * 
   * @param answer - Agent's response
   * @returns string[] - Array of suggestion texts
   */
  private getDefaultSuggestionPills(answer: string): string[] {
    try {
      const lowerAnswer = answer.toLowerCase();
      
      if (lowerAnswer.includes('requirement') || lowerAnswer.includes('compliance') || lowerAnswer.includes('regulation')) {
        return ["What are the requirements?", "How do I comply?", "Any exceptions?"];
      } else if (lowerAnswer.includes('process') || lowerAnswer.includes('procedure') || lowerAnswer.includes('step')) {
        return ["What's the next step?", "How long does it take?", "What documents are needed?"];
      } else if (lowerAnswer.includes('approval') || lowerAnswer.includes('submit')) {
        return ["How do I submit?", "What's the timeline?", "Who approves this?"];
      } else if (answer.length < 100) {
        return ["Tell me more", "Can you explain further?", "Any examples?"];
      }
      
      return ["What are the key points?", "Tell me more", "Any examples?", "What else should I know?"];
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error generating pills', 'getDefaultSuggestionPills', error);
      return [];
    }
  }

  /**
   * Set response_id on the latest message in chat history.
   * Called when the conversation_saved event provides the backend pk_chathistoryid
   * so that the share feature can use it.
   *
   * @param responseId - Backend response_id (pk_chathistoryid)
   */
  setLatestMessageResponseId(responseId: string): void {
    try {
      const currentHistory = this.agentChatHistory.value;
      if (currentHistory.length === 0 || !responseId) return;

      const updatedHistory = [...currentHistory];
      updatedHistory[updatedHistory.length - 1].response_id = responseId;
      this.agentChatHistory.next(updatedHistory);
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error setting response_id', 'setLatestMessageResponseId', error);
    }
  }

  /**
   * Get current agent chat history
   * 
   * @returns AgentChatMessage[] - Array of chat messages
   */
  getAgentChatHistory(): AgentChatMessage[] {
    try {
      return this.agentChatHistory.value;
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error getting chat history', 'getAgentChatHistory', error);
      return [];
    }
  }

  /**
   * Clear agent chat history
   */
  clearAgentChatHistory(): void {
    try {
      this.agentChatHistory.next([]);
      this.isNewAgentChat.next(true);
      this.historyCurrentPage = 1;
      this.historyTotalPages = 1;
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error clearing chat history', 'clearAgentChatHistory', error);
    }
  }

  /**
   * Start new chat for current agent
   * Clears history but keeps user in active chat interface
   */
  startNewChatForCurrentAgent(): void {
    try {
      this.agentChatHistory.next([]);
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error starting new chat', 'startNewChatForCurrentAgent', error);
    }
  }

  /**
   * Check if current state is new agent chat
   * 
   * @returns boolean - True if new chat
   */
  getIsNewAgentChat(): boolean {
    try {
      return this.isNewAgentChat.value;
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error checking new chat status', 'getIsNewAgentChat', error);
      return true;
    }
  }

  /**
   * Set new chat status
   * 
   * @param isNewChat - Whether this is a new chat
   */
  setIsNewChat(isNewChat: boolean): void {
    try {
      this.isNewAgentChat.next(isNewChat);
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error setting new chat status', 'setIsNewChat', error);
    }
  }

  /**
   * Update entire chat history
   * Used when loading history from API
   * 
   * @param history - Array of messages to set as history
   */
  updateChatHistory(history: any[]): void {
    try {
      this.agentChatHistory.next(history);
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error updating chat history', 'updateChatHistory', error);
    }
  }

  /**
   * Trigger session creation event
   * Also updates the cached thread list so the header "New Topic" dropdown shows the current session when sidebar is closed
   * 
   * @param threadName - Name of the new thread
   * @param threadId - Generated thread ID
   */
  triggerNewSessionCreation(threadName: string, threadId: string): void {
    try {
      this.currentThreadId = threadId;
      this.currentThreadIdSubject.next(threadId);
      this.currentThreadName = threadName || null;

      // Include agentId so subscribers can verify the session belongs to the correct agent
      const agentId = this.currentAgentId.value;
      this.newSessionCreated.next({ threadName, threadId, agentId });

      // Update cache so header dropdown shows current session even when sidebar is closed (session-history not in DOM)
      if (agentId && agentId !== 'module' && agentId !== 'agent_hub') {
        const cached = this.cachedAgentThreads.get(agentId) || [];
        const alreadyInList = cached.some((t: any) => t.thread_id === threadId);
        if (!alreadyInList) {
          const newThread = {
            thread_id: threadId,
            thread_name: threadName || 'New conversation',
            agent_model: agentId,
            message_count: 1,
            created_at: new Date().toISOString(),
            modified_at: new Date().toISOString(),
            last_question: threadName || 'New conversation',
            is_selected: true
          };
          const updated = [newThread, ...cached.map((t: any) => ({ ...t, is_selected: false }))];
          this.cachedAgentThreads.set(agentId, updated);
        }
      }
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error triggering session creation', 'triggerNewSessionCreation');
    }
  }


  /**
   * Get current thread ID
   * 
   * @returns string | null - Current thread ID or null
   */
  getCurrentThreadId(): string | null {
    return this.currentThreadId;
  }

  /**
   * Set current thread ID
   * 
   * @param threadId - Thread ID to set as current
   */
  setCurrentThreadId(threadId: string | null): void {
    this.currentThreadId = threadId;
    this.currentThreadName = null; // Name only used for just-created session
    this.currentThreadIdSubject.next(threadId);
  }

  /**
   * Get display name for current thread (set when session was just created; used when sidebar opens to show current session in list)
   */
  getCurrentThreadName(): string | null {
    return this.currentThreadName;
  }

  /** True when user selected an existing session (sidebar/header) – container should not animate last message when history loads. */
  private sessionLoadInProgress = false;

  setSessionLoadInProgress(value: boolean): void {
    this.sessionLoadInProgress = value;
  }

  getSessionLoadInProgress(): boolean {
    return this.sessionLoadInProgress;
  }

  /**
   * Check if streaming is active and show confirmation popup if needed
   * Otherwise execute action immediately
   * 
   * @param action - Function to execute after confirmation or immediately
   * @param type - Type of action: 'new_chat', 'session_switch', or 'chatbot_switch'
   */
  checkStreamingAndExecute(action: () => void, type: 'new_chat' | 'session_switch' | 'chatbot_switch' | 'agent_switch' = 'new_chat'): void {
    try {
      if (this.streamingStateService.getCurrentStreamingState()) {
        // Store pending action, type, and show popup
        this.pendingAction = action;
        this.streamingConfirmationType = type;
        this.showStreamingConfirmationPopup.next(true);
        this.logger.logInfo('[AgentOrchestrator] Streaming active - showing confirmation popup', 'checkStreamingAndExecute');
      } else {
        // Execute immediately if not streaming
        action();
      }
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error checking streaming state', 'checkStreamingAndExecute', error);
    }
  }

  /**
   * Confirm streaming action - stop streaming and execute pending action
   */
  confirmStreamingAction(): void {
    try {
      this.showStreamingConfirmationPopup.next(false);
      
      // Clean up minimal thread when confirming a session switch or agent switch.
      const currentAgent = this.getCurrentAgentId();
      if ((this.streamingConfirmationType === 'session_switch' || this.streamingConfirmationType === 'agent_switch') && this.currentThreadId) {
        const responseCount = (this.agentChatHistory.value || []).length;

        // If only one response exists in this thread, delete the thread via API
        if (responseCount === 1) {
          const req = {
            thread_id: this.currentThreadId,
            user_id: environment.LoginUserID || '',
            tenant_id: environment.TenantId || ''
          };
          this.deleteAgentThreadService.DeleteAgentThread(req, currentAgent).subscribe({
            next: (res) => {
              if (res?.status?.toLowerCase() === 'success') {
                this.logger.logInfo(
                  `[AgentOrchestrator] Deleted minimal thread ${this.currentThreadId} successfully`,
                  'confirmStreamingAction'
                );
              } else {
                this.logger.logInfo(
                  `[AgentOrchestrator] Failed to delete minimal thread ${this.currentThreadId}: ${res?.message || 'unknown'}`,
                  'confirmStreamingAction'
                );
              }
            },
            error: (err) => {
              this.logger.logInfo(
                `[AgentOrchestrator] Error deleting minimal thread ${this.currentThreadId}: ${err?.message || 'unknown'}`,
                'confirmStreamingAction'
              );
            }
          });
        }
      }
      
      if (this.pendingAction) {
        // Remove current session if it has minimal chat history (same as chatbot logic)
        this.removeCurrentSessionIfMinimalHistory();
        
        // Stop streaming first (similar to chatbot's stopStreaming call)
        if (this.stopStreamingCallback) {
          this.stopStreamingCallback();
        } else {
          // Fallback to just setting state if no callback registered
          this.streamingStateService.setStreamingState(false);
        }
        
        // Execute pending action
        this.pendingAction();
        this.pendingAction = null;
        
        this.logger.logInfo('[AgentOrchestrator] Streaming action confirmed and executed', 'confirmStreamingAction');
      }
      
      this.streamingConfirmationType = 'new_chat'; // Reset to default
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error confirming streaming action', 'confirmStreamingAction', error);
    }
  }

  /**
   * Cancel streaming action - hide popup and clear pending action
   */
  cancelStreamingAction(): void {
    try {
      this.showStreamingConfirmationPopup.next(false);
      this.pendingAction = null;
      this.streamingConfirmationType = 'new_chat'; // Reset to default
      this.logger.logInfo('[AgentOrchestrator] Streaming action cancelled', 'cancelStreamingAction');
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error cancelling streaming action', 'cancelStreamingAction', error);
    }
  }

  /**
   * Get current streaming confirmation type for popup messages
   */
  getStreamingConfirmationType(): 'new_chat' | 'session_switch' | 'chatbot_switch' | 'agent_switch' {
    return this.streamingConfirmationType;
  }

  /**
   * Register callback for stopping streaming
   * Called by agent-chat-container component to register its stopStreaming method
   * 
   * @param callback - Function to stop streaming subscriptions
   */
  registerStopStreamingCallback(callback: (() => void) | null): void {
    try {
      this.stopStreamingCallback = callback;
      this.logger.logInfo('[AgentOrchestrator] Stop streaming callback registered', 'registerStopStreamingCallback');
    } catch (error: any) {
      this.logger.logError(`Error registering stop streaming callback: ${error}`, 'AgentOrchestratorService.registerStopStreamingCallback');
    }
  }

  /**
   * Register callback for lazy loading chat history
   * Called by chat-sessions component to register its loadMore method
   * 
   * @param callback - Function that returns true if load started, false otherwise
   */
  registerLazyLoadCallback(callback: (() => boolean) | null): void {
    try {
      this.lazyLoadHistoryCallback = callback;
      this.logger.logInfo('[AgentOrchestrator] Lazy load callback registered', 'registerLazyLoadCallback');
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error registering lazy load callback', 'registerLazyLoadCallback', error);
    }
  }

  /**
   * Trigger lazy load of chat history
   * Called by agent-chat-interface when user scrolls to top
   * 
   * @returns boolean - True if load started, false if no callback or load failed
   */
  triggerLazyLoad(): boolean {
    try {
      if (this.lazyLoadHistoryCallback) {
        return this.lazyLoadHistoryCallback();
      }
      this.logger.logInfo('[AgentOrchestrator] No lazy load callback registered', 'triggerLazyLoad');
      return false;
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error triggering lazy load', 'triggerLazyLoad', error);
      return false;
    }
  }

  /**
   * Update lazy loading state
   * Called by chat-sessions to update loading indicators
   * 
   * @param isLoading - Whether currently loading more history
   * @param hasMore - Whether more pages are available
   */
  updateLazyLoadState(isLoading: boolean, hasMore: boolean): void {
    try {
      this.lazyLoadState.next({ isLoading, hasMore });
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error updating lazy load state', 'updateLazyLoadState', error);
    }
  }

  /**
   * Set loading session state (show "Loading session..." overlay when switching sessions).
   */
  setLoadingSession(loading: boolean): void {
    try {
      this.isLoadingSession.next(loading);
    } catch (error: any) {
      this.logger.logInfo('[AgentOrchestrator] Error setting loading session', 'setLoadingSession', error);
    }
  }

  /**
   * Get current lazy load state
   * 
   * @returns Object with isLoading and hasMore flags
   */
  getLazyLoadState(): {isLoading: boolean, hasMore: boolean} {
    return this.lazyLoadState.value;
  }

  /**
   * Update centralized history pagination state.
   * Called by both agent-chat-container (header session switch) and
   * chat-sessions (sidebar session switch) after loading history.
   */
  updateHistoryPagination(currentPage: number, totalPages: number): void {
    this.historyCurrentPage = currentPage;
    this.historyTotalPages = totalPages;
  }

  /**
   * Get current history pagination state.
   */
  getHistoryPagination(): { currentPage: number; totalPages: number } {
    return { currentPage: this.historyCurrentPage, totalPages: this.historyTotalPages };
  }

  /**
   * Remove current agent session if it has minimal chat history (1 or fewer messages)
   * Similar logic to chatbot's removeCurrentSessionIfSingleQuestion()
   */
  private removeCurrentSessionIfMinimalHistory(): void {
    try {
      const currentHistory = this.agentChatHistory.getValue();
      
      // Check if current session has 1 or fewer chat messages and remove it
      if (currentHistory.length <= 1 && this.currentThreadId) {
        this.logger.logInfo(`[AgentOrchestrator] Removing session with minimal history: ${this.currentThreadId}`, 'removeCurrentSessionIfMinimalHistory');
        
        // Emit event to notify session history component to remove this thread
        this.sessionRemovalRequested.next({
          threadId: this.currentThreadId
        });
      }
    } catch (error: any) {
      this.logger.logError(`Error removing session with minimal history: ${error}`, 'AgentOrchestratorService.removeCurrentSessionIfMinimalHistory');
    }
  }
}

